import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  Checkbox,
  Button,
} from "flowbite-react";
import { useNavigate } from "react-router-dom";
import { HiArrowUp, HiArrowDown } from "react-icons/hi";
import { useTranslation } from "@repo/core";
import type { Strategy } from "@repo/api";
import { formatDecimal, formatPercentage } from "../../../utils";
import type { IndicatorDetail } from "../../../hooks/api/useUniversesApi";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import {
  useFloating,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  FloatingArrow,
  FloatingPortal,
} from "@floating-ui/react";

// Description tooltip for indicator names. Rendered via a portal to
// document.body so it isn't clipped by the table wrapper's forced
// `overflow-y: hidden` (needed to suppress an unwanted vertical scrollbar
// caused by the horizontally-scrollable table container).
const IndicatorDescriptionTooltip = ({
  content,
  children,
}: {
  content: ReactNode;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const arrowRef = useRef<SVGSVGElement>(null);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "top",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()} className="w-full">
        {children}
      </div>
      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 9999 }}
            {...getFloatingProps()}
            className="max-w-[300px] whitespace-normal break-words rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium leading-normal text-white shadow-sm"
          >
            {content}
            <FloatingArrow ref={arrowRef} context={context} fill="#111827" />
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export type SortField =
  | "Sharpe"
  | "Volatility"
  | "CAGR"
  | "meanTurnover"
  | "name";
export type SortDirection = "asc" | "desc";

export interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

interface IndicatorsTableProps {
  strategies: Strategy[];
  isLoading?: boolean;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  itemsPerPage: number;
  indicatorDetails?: Record<string, IndicatorDetail>;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  selectedPeriod?: string;
  selectedUniverse?: string;
  className?: string;
  sortConfig?: SortConfig;
  onSortChange?: (sortConfig: SortConfig) => void;
  selectedStrategyIds?: Set<string>;
  onSelectionChange?: (strategyId: string, selected: boolean) => void;
  onClearSelection?: () => void;
  onReset?: () => void;
}

/**
 * Indicators table component with pagination and selection
 */
export const IndicatorsTable = ({
  strategies,
  isLoading = false,
  currentPage,
  totalPages,
  totalCount,
  itemsPerPage,
  indicatorDetails,
  onPageChange,
  onItemsPerPageChange,
  selectedPeriod,
  selectedUniverse,
  className = "",
  sortConfig,
  onSortChange,
  selectedStrategyIds = new Set(),
  onSelectionChange,
  onClearSelection,
  onReset,
}: IndicatorsTableProps) => {
  const { t, tMimicking } = useTranslation();
  const navigate = useNavigate();

  const handleSort = (field: SortField) => {
    if (!onSortChange) return;

    const newDirection: SortDirection =
      sortConfig?.field === field && sortConfig?.direction === "desc"
        ? "asc"
        : "desc";

    onSortChange({ field, direction: newDirection });
  };

  const getSortIcon = (field: SortField) => {
    if (sortConfig?.field !== field) {
      return null;
    }
    return sortConfig.direction === "asc" ? (
      <HiArrowUp className="inline ml-1 w-3 h-3" />
    ) : (
      <HiArrowDown className="inline ml-1 w-3 h-3" />
    );
  };

  // Helper component for clickable strategy name with description tooltip
  const ClickableStrategyName = ({
    strategy,
    name,
    description,
  }: {
    strategy: Strategy;
    name?: string;
    description?: string;
  }) => {
    // Prefer the localized characteristic name from indicatorDetails; fall
    // back to the strategy's own name if the indicator isn't found there.
    const displayName = name || strategy.name;

    if (!description) {
      return (
        <div className="truncate" title={displayName}>
          {displayName}
        </div>
      );
    }

    return (
      <IndicatorDescriptionTooltip content={description}>
        {/* No native title attribute here - the Tooltip above already
            shows displayName's full content on hover, and a title would
            trigger a second, redundant browser-native tooltip in some
            browsers (Chrome's native tooltip delay just tends to mask it). */}
        <div className="truncate cursor-pointer hover:text-blue-600 transition-colors underline decoration-dotted hover:decoration-solid w-full relative z-50">
          {displayName}
        </div>
      </IndicatorDescriptionTooltip>
    );
  };

  const handleDetailClick = (strategy: Strategy) => {
    // Convert selected period to URL-friendly format
    let dateRange = selectedPeriod?.toLowerCase();
    if (selectedPeriod === "Custom") {
      dateRange = "custom";
    }

    // Convert strategy name to URL-friendly format (lowercase, replace spaces with dashes)

    // Navigate to the delivered strategy page
    navigate(`/delivered/${selectedUniverse}/${dateRange}/${strategy.modelId}`);
  };

  const renderPaginationButton = (page: number, isActive: boolean = false) => (
    <button
      key={page}
      onClick={() => onPageChange(page)}
      className={`px-3 py-2 text-sm rounded-md transition-colors ${isActive ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-100"
        }`}
      disabled={isLoading}
    >
      {page}
    </button>
  );

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;

    // Always show first page
    pages.push(renderPaginationButton(1, currentPage === 1));

    let startPage = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3);

    // Adjust start page if we're near the end
    if (endPage - startPage < maxVisiblePages - 3) {
      startPage = Math.max(2, endPage - maxVisiblePages + 3);
    }

    // Add ellipsis after first page if needed
    if (startPage > 2) {
      pages.push(
        <span key="ellipsis-start" className="px-3 py-2 text-gray-400">
          ...
        </span>
      );
    }

    // Add middle pages
    for (let page = startPage; page <= endPage; page++) {
      pages.push(renderPaginationButton(page, currentPage === page));
    }

    // Add ellipsis before last page if needed
    if (endPage < totalPages - 1) {
      pages.push(
        <span key="ellipsis-end" className="px-3 py-2 text-gray-400">
          ...
        </span>
      );
    }

    // Always show last page if more than 1 page
    if (totalPages > 1) {
      pages.push(
        renderPaginationButton(totalPages, currentPage === totalPages)
      );
    }

    return (
      <div className="flex flex-col gap-4 mt-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-sm text-gray-700 whitespace-nowrap">
            {t("factorMimicking.indicatorsTable.pagination.counter", {
              from: (currentPage - 1) * itemsPerPage + 1,
              to: Math.min(currentPage * itemsPerPage, totalCount),
              total: totalCount,
            })}
          </span>
          <select
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={isLoading}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-1 justify-center sm:justify-end">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || isLoading}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {tMimicking("indicatorsTable.pagination.previous")}
          </button>
          {pages}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages || isLoading}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {tMimicking("indicatorsTable.pagination.next")}
          </button>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={`space-y-4 ${className}`}>
        {/* Loading skeleton */}
        <div className="animate-pulse">
          <div className="h-12 bg-gray-200 rounded mb-4"></div>
          {[1, 2, 3, 4, 5].map((index) => (
            <div key={index} className="h-16 bg-gray-100 rounded mb-2"></div>
          ))}
        </div>
      </div>
    );
  }

  if (strategies.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="text-gray-500">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {tMimicking("indicatorsTable.noResults.title")}
          </h3>
          <p className="text-gray-600">
            {tMimicking("indicatorsTable.noResults.descriptionPrefix")}
            <button
              onClick={onReset}
              className="text-blue-600 hover:text-blue-800 underline cursor-pointer font-medium"
            >
              {tMimicking("indicatorsTable.noResults.descriptionLink")}
            </button>
            {tMimicking("indicatorsTable.noResults.descriptionSuffix")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={className} data-tour="indicators-table">
      {/* Selection Summary */}
      {selectedStrategyIds.size > 0 && (
        <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-blue-900">
              {selectedStrategyIds.size === 1
                ? t("factorMimicking.indicatorsTable.selection.summary", { count: selectedStrategyIds.size })
                : t("factorMimicking.indicatorsTable.selection.summary_plural", { count: selectedStrategyIds.size })}
            </span>
            <span className="text-xs text-blue-700">
              {tMimicking("indicatorsTable.selection.acrossPages")}
            </span>
          </div>
          <Button
            size="xs"
            color="light"
            onClick={onClearSelection}
          >
            {tMimicking("indicatorsTable.selection.clearSelection")}
          </Button>
        </div>
      )}

      <style>{`
        /* Sticky action column */
        .sticky-action-header {
          position: sticky !important;
          right: 0 !important;
          background-color: #f9fafb !important;
          z-index: 10 !important;
          box-shadow: -2px 0 4px rgba(0, 0, 0, 0.05) !important;
        }
        .sticky-action-cell {
          position: sticky !important;
          right: 0 !important;
          background-color: white !important;
          z-index: 5 !important;
          box-shadow: -2px 0 4px rgba(0, 0, 0, 0.05) !important;
        }
        .sticky-action-cell:hover {
          background-color: #f9fafb !important;
        }
        /* Remove vertical scrollbar from table wrapper */
        [data-testid="table-element"] {
          overflow-y: hidden !important;
        }
      `}</style>

      {/* Scrollable table container */}
      <div className="overflow-x-auto">
        <Table hoverable className="text-sm">
          <TableHead>
            <TableRow>
              <TableHeadCell className="px-2 py-2 text-xs w-12">
                {/* Select */}
              </TableHeadCell>
              <TableHeadCell className="px-2 py-2 text-xs">
                {tMimicking("indicatorsTable.table.indicator")}
              </TableHeadCell>
              <TableHeadCell className="px-2 py-2 text-xs">
                {tMimicking("indicatorsTable.table.style")}
              </TableHeadCell>
              <TableHeadCell
                className="px-2 py-2 text-xs text-center cursor-pointer hover:bg-gray-50 select-none"
                onClick={() => handleSort("Sharpe")}
              >
                {tMimicking("indicatorsTable.table.sharpe")}
                {getSortIcon("Sharpe")}
              </TableHeadCell>
              <TableHeadCell
                className="px-2 py-2 text-xs text-center cursor-pointer hover:bg-gray-50 select-none"
                onClick={() => handleSort("Volatility")}
              >
                {tMimicking("indicatorsTable.table.volatility")}
                {getSortIcon("Volatility")}
              </TableHeadCell>
              <TableHeadCell
                className="px-2 py-2 text-xs text-center cursor-pointer hover:bg-gray-50 select-none"
                onClick={() => handleSort("CAGR")}
              >
                {tMimicking("indicatorsTable.table.CAGR")}
                {getSortIcon("CAGR")}
              </TableHeadCell>
              <TableHeadCell
                className="px-2 py-2 text-xs text-center cursor-pointer hover:bg-gray-50 select-none"
                onClick={() => handleSort("meanTurnover")}
              >
                {tMimicking("indicatorsTable.table.meanTurnover")}
                {getSortIcon("meanTurnover")}
              </TableHeadCell>
              <TableHeadCell className="px-2 py-2 text-xs sticky-action-header">
                <span className="sr-only">
                  {tMimicking("indicatorsTable.table.actionHeader")}
                </span>
              </TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody className="divide-y">
            {strategies.map((strategy) => (
              <TableRow
                key={strategy.modelId}
                className="bg-white dark:border-gray-700 dark:bg-gray-800 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => handleDetailClick(strategy)}
              >
                <TableCell
                  className="px-2 py-2 w-12"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={selectedStrategyIds.has(strategy.modelId)}
                    onChange={(e) => {
                      e.stopPropagation();
                      onSelectionChange?.(strategy.modelId, e.target.checked);
                    }}
                  />
                </TableCell>
                <TableCell className="px-2 py-2 font-medium text-gray-900 dark:text-white text-sm max-w-[200px] truncate">
                  <ClickableStrategyName
                    strategy={strategy}
                    name={
                      indicatorDetails?.[Object.keys(strategy.factor)[0]]
                        ?.name
                    }
                    description={
                      indicatorDetails?.[Object.keys(strategy.factor)[0]]
                        ?.description
                    }
                  />
                </TableCell>
                <TableCell className="px-2 py-2">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium">
                    {tMimicking(`filters.style.${strategy.style}`) || "N/A"}
                  </span>
                </TableCell>
                <TableCell className="px-2 py-2 text-center">
                  <span
                    className={`font-semibold text-sm ${strategy.Sharpe > 1 ? "text-green-600" : "text-gray-600"
                      }`}
                  >
                    {formatDecimal(strategy.Sharpe, 2)}
                  </span>
                </TableCell>
                <TableCell className="px-2 py-2 text-center">
                  <span
                    className={`font-semibold text-sm ${strategy.Volatility < 15
                      ? "text-green-600"
                      : strategy.Volatility < 25
                        ? "text-yellow-600"
                        : "text-red-600"
                      }`}
                  >
                    {formatPercentage(strategy.Volatility)}
                  </span>
                </TableCell>
                <TableCell className="px-2 py-2 text-center">
                  <span
                    className={`font-semibold text-sm ${strategy.CAGR > 10
                      ? "text-green-600"
                      : strategy.CAGR > 0
                        ? "text-gray-600"
                        : "text-red-600"
                      }`}
                  >
                    {strategy.CAGR > 0 ? "+" : ""}
                    {formatPercentage(strategy.CAGR)}
                  </span>
                </TableCell>
                <TableCell className="px-2 py-2 text-center">
                  <span className={`font-semibold text-sm`}>
                    {strategy.meanTurnover > 0 ? "+" : ""}
                    {formatPercentage(strategy.meanTurnover)}
                  </span>
                </TableCell>
                <TableCell className="px-2 py-2 sticky-action-cell">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDetailClick(strategy);
                    }}
                    className="px-2 py-1 rounded text-xs font-medium focus:outline-none focus:ring-1 focus:ring-blue-300 transition-colors border hover:shadow-sm cursor-pointer"
                    style={{
                      backgroundColor: "#EBF5FF",
                      borderColor: "#A4CAFE",
                      color: "#1A55DB",
                    }}
                  >
                    {tMimicking("indicatorsTable.table.actionButton")}
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {renderPagination()}
    </div>
  );
};

export default IndicatorsTable;
